/**
 * Privacy Policy Page
 *
 * Public page - accessible without authentication. Linked from the login
 * footer and used as the privacy policy URL for the CCMS mobile apps on the
 * Apple App Store and Google Play.
 */

import { Link } from 'react-router-dom';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Anchor } from '@/components/ui/core';
import { Divider } from '@/components/ui/data-display';
import { IconShieldLock } from '@tabler/icons-react';
import LegalPageLayout from './LegalPageLayout';

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="30 June 2026"
      current="privacy"
      icon={<IconShieldLock size={44} color="var(--mantine-color-indigo-6)" />}
    >
      {/* 1. Introduction */}
      <section>
        <Title order={3} mb="sm">1. Introduction</Title>
        <Text size="sm" mb="xs">
          This Privacy Policy explains how Acts Christian Church ("Acts Church", "we", "our",
          or "us") collects, uses, and protects your personal information when you use the CCMS
          apps — our suite of church community and ministry applications (such as the staff,
          tasks, chat, bookings, announcements, and resources apps) and this web portal.
        </Text>
        <Text size="sm" mb="xs">
          These apps are provided for the members, staff, leaders, volunteers, and other
          authorised users of Acts Christian Church. They complement our church-wide{' '}
          <Anchor href="https://actschurch.co.za/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </Anchor>
          , which governs our broader handling of personal information.
        </Text>
        <Text size="sm">
          We are based in South Africa and process personal information in accordance with the
          Protection of Personal Information Act, 2013 (POPIA).
        </Text>
      </section>

      <Divider />

      {/* 2. Information we collect */}
      <section>
        <Title order={3} mb="sm">2. Information We Collect</Title>
        <Text size="sm" fw={500} mb="xs">2.1. Information you provide:</Text>
        <Stack gap="xs" mb="md">
          <Text size="sm">- Account and profile details (name, email address, phone number, profile photo)</Text>
          <Text size="sm">- Your role, ministry, campus, or group within the church</Text>
          <Text size="sm">- Content you submit (such as messages, tasks, bookings, files, and requests)</Text>
        </Stack>

        <Text size="sm" fw={500} mb="xs">2.2. Information collected automatically:</Text>
        <Stack gap="xs">
          <Text size="sm">- Device information (device model, operating system, app version)</Text>
          <Text size="sm">- Usage and diagnostic data (features used, errors, performance)</Text>
          <Text size="sm">- IP address and general location derived from it</Text>
          <Text size="sm">- Push notification tokens (so we can deliver notifications to your device)</Text>
        </Stack>
      </section>

      <Divider />

      {/* 3. How we use your information */}
      <section>
        <Title order={3} mb="sm">3. How We Use Your Information</Title>
        <Text size="sm" mb="xs">We use your information to:</Text>
        <Stack gap="xs">
          <Text size="sm">- Provide, operate, and secure the apps</Text>
          <Text size="sm">- Authenticate you and keep you signed in</Text>
          <Text size="sm">- Deliver features such as tasks, chat, bookings, and notifications</Text>
          <Text size="sm">- Respond to your requests and provide support</Text>
          <Text size="sm">- Improve and maintain the apps</Text>
          <Text size="sm">- Comply with our legal and accounting obligations</Text>
        </Stack>
      </section>

      <Divider />

      {/* 4. Push notifications */}
      <section>
        <Title order={3} mb="sm">4. Push Notifications</Title>
        <Text size="sm">
          With your permission, we send push notifications to your device for service-related
          messages (for example, a new task, message, or booking update). We use device push
          tokens provided by the platform notification services (such as Apple Push Notification
          service and Firebase Cloud Messaging). You can disable notifications at any time in
          your device settings.
        </Text>
      </section>

      <Divider />

      {/* 5. Sharing */}
      <section>
        <Title order={3} mb="sm">5. How We Share Information</Title>
        <Text size="sm" mb="xs">We may share your information with:</Text>
        <Stack gap="xs">
          <Text size="sm">
            <strong>Service providers:</strong> Trusted providers who help us operate the apps
            (such as cloud hosting, messaging and push delivery, and email or SMS services),
            under confidentiality obligations.
          </Text>
          <Text size="sm">
            <strong>Legal requirements:</strong> When required by law, regulation, or a valid
            legal request, or to protect our rights and the safety of others.
          </Text>
        </Stack>
        <Text size="sm" mt="xs">
          We do not sell your personal information.
        </Text>
      </section>

      <Divider />

      {/* 6. Data security */}
      <section>
        <Title order={3} mb="sm">6. Data Security</Title>
        <Stack gap="xs">
          <Text size="sm">
            6.1. We use appropriate technical and organisational measures to protect your data,
            including encryption in transit and access controls.
          </Text>
          <Text size="sm">
            6.2. We review and update our security practices to address new risks.
          </Text>
          <Text size="sm">
            6.3. No method of transmission or storage is completely secure, so we cannot
            guarantee absolute security.
          </Text>
        </Stack>
      </section>

      <Divider />

      {/* 7. Data retention */}
      <section>
        <Title order={3} mb="sm">7. Data Retention</Title>
        <Text size="sm">
          We retain your personal information for as long as your account or relationship with the
          church is active, and thereafter only as needed to meet our legal, accounting, or
          legitimate operational requirements. You may request deletion of your data, subject to
          these requirements.
        </Text>
      </section>

      <Divider />

      {/* 8. Your rights */}
      <section>
        <Title order={3} mb="sm">8. Your Rights</Title>
        <Text size="sm" mb="xs">Under POPIA, you have the right to:</Text>
        <Stack gap="xs">
          <Text size="sm">- Access the personal information we hold about you</Text>
          <Text size="sm">- Request correction of inaccurate or incomplete information</Text>
          <Text size="sm">- Request deletion of your information</Text>
          <Text size="sm">- Object to or request restriction of certain processing</Text>
          <Text size="sm">- Withdraw consent where processing is based on consent</Text>
          <Text size="sm">- Lodge a complaint with the Information Regulator (South Africa)</Text>
        </Stack>
        <Text size="sm" mt="xs">
          To request deletion of your information, you can use our{' '}
          <Anchor component={Link} to="/data-deletion">data deletion request form</Anchor>.
        </Text>
      </section>

      <Divider />

      {/* 9. Children */}
      <section>
        <Title order={3} mb="sm">9. Children's Privacy</Title>
        <Text size="sm">
          The apps are intended for authorised users within our church community and are not
          directed at young children. Where information relating to minors is processed (for
          example, as part of children's or youth ministry), it is handled by a parent, guardian,
          or the church in line with our church-wide Privacy Policy.
        </Text>
      </section>

      <Divider />

      {/* 10. Changes */}
      <section>
        <Title order={3} mb="sm">10. Changes to This Policy</Title>
        <Text size="sm">
          We may update this Privacy Policy from time to time. The "Last updated" date at the top
          shows when it was last revised. Where changes are significant, we will provide notice
          through the apps or by other appropriate means.
        </Text>
      </section>

      <Divider />

      {/* 11. Contact */}
      <section>
        <Title order={3} mb="sm">11. Contact Us</Title>
        <Text size="sm" mb="xs">
          For questions about this Privacy Policy or to exercise your rights, please contact:
        </Text>
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            App & data queries:{' '}
            <Anchor href="mailto:developer@actschurch.co.za">developer@actschurch.co.za</Anchor>
          </Text>
          <Text size="sm">
            Acts Christian Church<br />
            241 Pretorius Street, Vorna Valley, Midrand, 1686, South Africa<br />
            Tel: +27 11 805 2381 ·{' '}
            <Anchor href="mailto:info@actschurch.co.za">info@actschurch.co.za</Anchor>
          </Text>
        </Stack>
      </section>
    </LegalPageLayout>
  );
}
